import React, { useState } from 'react';
import { Box, Button, Menu, MenuItem, Snackbar } from '@mui/material';
import { ContentCopy as CopyIcon, KeyboardArrowDown as ArrowDownIcon } from '@mui/icons-material';
import { translateAnswerText } from '../utils/questionnaireApi';
import { isEnglishQuestionLocale } from '../utils/locale';
import { colors } from '../../../shared/styles/theme';

type Props = {
  text: string;
  questionLocale?: string | null;
  qafId?: string | null;
  /** `legacy` matches ViewClientQuestions (#156C86); default uses theme primaryHover. */
  accent?: 'theme' | 'legacy';
};

/**
 * Primary Copy (English) plus optional chevron menu for "Copy in original language".
 */
export const CopyAnswerWithOriginalMenu: React.FC<Props> = ({
  text,
  questionLocale,
  qafId,
  accent = 'theme',
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [translating, setTranslating] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const showOriginalOption = Boolean(questionLocale) && !isEnglishQuestionLocale(questionLocale);

  const handleCopyEnglish = () => {
    void navigator.clipboard.writeText(text);
  };

  const handleCopyOriginal = async () => {
    if (!questionLocale) return;
    setTranslating(true);
    try {
      const res = await translateAnswerText({
        text,
        target_locale: questionLocale,
        qaf_id: qafId ?? undefined,
      });
      await navigator.clipboard.writeText(res.translated_text);
      setMenuAnchor(null);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Translation failed';
      setSnackbar(msg);
    } finally {
      setTranslating(false);
    }
  };

  const c = accent === 'legacy' ? '#156C86' : colors.primaryHover;
  const btnSx = {
    textTransform: 'none' as const,
    fontSize: '0.8rem',
    minWidth: 'auto',
    px: 2,
    py: 0.5,
    borderRadius: 0,
    borderColor: c,
    color: c,
    '& .MuiSvgIcon-root': { color: c },
    '&:hover': {
      borderColor: c,
      color: c,
      backgroundColor: 'rgba(21,108,134,0.10)',
    },
    '&:active': {
      borderColor: c,
      color: c,
      backgroundColor: 'rgba(21,108,134,0.20)',
    },
  };

  return (
    <>
      <Box sx={{ display: 'inline-flex', alignItems: 'stretch' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CopyIcon />}
          onClick={handleCopyEnglish}
          sx={{
            ...btnSx,
            borderTopRightRadius: showOriginalOption ? 0 : undefined,
            borderBottomRightRadius: showOriginalOption ? 0 : undefined,
          }}
        >
          Copy
        </Button>
        {showOriginalOption && (
          <>
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              aria-label="More copy options"
              sx={{
                ...btnSx,
                minWidth: 36,
                px: 0.5,
                borderLeft: 'none',
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
              }}
            >
              <ArrowDownIcon fontSize="small" />
            </Button>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                disabled={translating}
                onClick={() => {
                  void handleCopyOriginal();
                }}
              >
                Copy in original language
              </MenuItem>
            </Menu>
          </>
        )}
      </Box>
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};
